import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

function StoreSetup({ user, onLogout }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [setups, setSetups] = useState([]);
  const [selectedSetup, setSelectedSetup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSetupForm, setShowSetupForm] = useState(false);
  const [error, setError] = useState('');
  const [setupForm, setSetupForm] = useState({ franchisee_id: '', store_code: '', store_name: '', expected_launch_date: '', setup_manager: '' });

  const tabs = [
    { id: 'overview', name: 'Overview', icon: '📊' },
    { id: 'design', name: 'Design Approval', icon: '🎨' },
    { id: 'stock', name: 'Stock Allocation', icon: '📦' },
    { id: 'training', name: 'Training', icon: '👨‍🎓' },
    { id: 'checklist', name: 'Launch Checklist', icon: '✅' }
  ];

  useEffect(() => {
    fetchSetups();
  }, []);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) setActiveTab(tab);
  }, [searchParams]);

  const fetchSetups = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/store-setup`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSetups(response.data.setups || []);
      if (response.data.setups && response.data.setups.length > 0) {
        setSelectedSetup(response.data.setups[0]);
      }
    } catch (error) {
      console.error('Error fetching setups:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  const createSetup = async (event) => {
    event.preventDefault();
    try {
      setError('');
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      await axios.post(`${API}/api/store-setup`, setupForm, { headers: { Authorization: `Bearer ${token}` } });
      setShowSetupForm(false);
      setSetupForm({ franchisee_id: '', store_code: '', store_name: '', expected_launch_date: '', setup_manager: '' });
      await fetchSetups();
    } catch (requestError) { setError(requestError.response?.data?.detail || 'Unable to create store setup.'); }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="p-6">
          <div className="text-center py-12">Loading store setups...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Store Setup & Launch</h1>
            <p className="mt-2 text-gray-600">Manage store setup from design to launch</p>
          </div>
          
          {/* Setup Selector */}
          {setups.length > 0 && (
            <div className="w-64">
              <label className="block text-sm font-medium text-gray-700 mb-1">Select Setup</label>
              <select
                value={selectedSetup?.setup_id || ''}
                onChange={(e) => {
                  const setup = setups.find(s => s.setup_id === e.target.value);
                  setSelectedSetup(setup);
                }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                {setups.map(setup => (
                  <option key={setup.setup_id} value={setup.setup_id}>
                    {setup.store_name} ({setup.store_code})
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {setups.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <div className="text-6xl mb-4">🏪</div>
            <h3 className="text-xl font-semibold mb-2">No Store Setups Found</h3>
            <p className="text-gray-600 mb-6">Create a new store setup project to get started</p>
            <button onClick={() => setShowSetupForm(true)} className="px-6 py-3 bg-[#2b3380] text-white rounded-lg hover:bg-[#1f2660]">
              + Create Store Setup
            </button>
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div className="border-b border-gray-200 mb-6 overflow-x-auto">
              <nav className="-mb-px flex space-x-8">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-[#2b3380] text-[#2b3380]'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <span className="mr-2">{tab.icon}</span>
                    {tab.name}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div>
              {activeTab === 'overview' && selectedSetup && (
                <OverviewTab setup={selectedSetup} />
              )}
              {activeTab === 'design' && selectedSetup && (
                <DesignTab setup={selectedSetup} />
              )}
              {activeTab === 'stock' && selectedSetup && (
                <StockTab setup={selectedSetup} />
              )}
              {activeTab === 'training' && selectedSetup && (
                <TrainingTab setup={selectedSetup} />
              )}
              {activeTab === 'checklist' && selectedSetup && (
                <ChecklistTab setup={selectedSetup} />
              )}
            </div>
          </>
        )}
        {showSetupForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="p-5 border-b flex items-center justify-between"><h2 className="text-xl font-bold">Create Store Setup</h2><button onClick={() => setShowSetupForm(false)} aria-label="Close">✕</button></div>
              <form onSubmit={createSetup} className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                {error && <div className="md:col-span-2 bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}
                {[['franchisee_id','Franchisee ID'],['store_code','Store code'],['store_name','Store name'],['setup_manager','Setup manager']].map(([key,label]) => <label key={key}><span className="block text-sm font-medium mb-1">{label} *</span><input value={setupForm[key]} onChange={(e) => setSetupForm({...setupForm,[key]:e.target.value})} required className="w-full border rounded-lg px-3 py-2" /></label>)}
                <label className="md:col-span-2"><span className="block text-sm font-medium mb-1">Expected launch date *</span><input type="date" value={setupForm.expected_launch_date} onChange={(e) => setSetupForm({...setupForm,expected_launch_date:e.target.value})} required className="w-full border rounded-lg px-3 py-2" /></label>
                <div className="md:col-span-2 flex justify-end gap-3"><button type="button" onClick={() => setShowSetupForm(false)} className="px-4 py-2 border rounded-lg">Cancel</button><button type="submit" className="px-4 py-2 bg-[#2b3380] text-white rounded-lg">Create setup</button></div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

// ==================== OVERVIEW TAB ====================
function OverviewTab({ setup }) {
  const getStatusColor = (status) => {
    const colors = {
      'Pending': 'bg-yellow-100 text-yellow-800',
      'In Progress': 'bg-[#2b338030] text-[#2b3380]',
      'Approved': 'bg-green-100 text-green-800',
      'Completed': 'bg-green-100 text-green-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const phases = [
    { name: 'Design Approval', status: 'Completed', progress: 100 },
    { name: 'Stock Allocation', status: 'In Progress', progress: 60 },
    { name: 'Training', status: 'Pending', progress: 0 },
    { name: 'Launch Preparation', status: 'Pending', progress: 0 },
  ];

  return (
    <div className="space-y-6">
      {/* Store Info Card */}
      <div className="bg-gradient-to-r from-[#2b3380] to-[#1f2660] rounded-lg shadow-lg p-6 text-white">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold">{setup.store_name}</h2>
            <p className="mt-1 opacity-90">{setup.store_code}</p>
          </div>
          <div className="text-right">
            <div className="text-sm opacity-90">Expected Launch</div>
            <div className="text-lg font-bold">
              {setup.expected_launch_date ? new Date(setup.expected_launch_date).toLocaleDateString() : 'TBD'}
            </div>
          </div>
        </div>
      </div>

      {/* Progress Overview */}
      <div className="grid grid-cols-4 gap-6">
        {phases.map((phase, index) => (
          <div key={index} className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-gray-900">{phase.name}</h3>
              <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(phase.status)}`}>
                {phase.status}
              </span>
            </div>
            <div className="mt-4">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-gray-600">Progress</span>
                <span className="font-semibold">{phase.progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-[#2b3380] h-2 rounded-full"
                  style={{ width: `${phase.progress}%` }}
                ></div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600 mb-2">Setup Manager</div>
          <div className="text-xl font-bold">{setup.setup_manager || 'Not Assigned'}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600 mb-2">Current Phase</div>
          <div className="text-xl font-bold">{setup.setup_phase}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600 mb-2">Overall Status</div>
          <div className="text-xl font-bold">
            <span className={`px-3 py-1 text-sm rounded-full ${getStatusColor(setup.overall_status)}`}>
              {setup.overall_status}
            </span>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Setup Timeline</h3>
        <div className="space-y-4">
          <div className="flex items-start">
            <div className="flex-shrink-0 w-32 text-sm text-gray-600">
              {new Date(setup.created_at).toLocaleDateString()}
            </div>
            <div className="flex-grow border-l-2 border-[#2b3380] pl-4 pb-4">
              <div className="font-semibold">Setup Created</div>
              <div className="text-sm text-gray-600">Store setup project initialized</div>
            </div>
          </div>
          <div className="flex items-start opacity-50">
            <div className="flex-shrink-0 w-32 text-sm text-gray-600">Upcoming</div>
            <div className="flex-grow border-l-2 border-gray-300 pl-4 pb-4">
              <div className="font-semibold">Design Approval</div>
              <div className="text-sm text-gray-600">Submit designs for approval</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== DESIGN TAB ====================
function DesignTab({ setup }) {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [message, setMessage] = useState('');
  const [form, setForm] = useState({ design_category: 'Interior', design_document_url: '', design_notes: '' });
  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken') || localStorage.getItem('token')}` });
  const load = async () => {
    try { setLoading(true); const response = await axios.get(`${API}/api/store-setup/${setup.setup_id}/design-approvals`, { headers: authHeaders() }); setApprovals(response.data.approvals || []); }
    catch (error) { setMessage(error.response?.data?.detail || 'Unable to load design approvals.'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [setup.setup_id]);
  const submit = async (event) => {
    event.preventDefault();
    try {
      await axios.post(`${API}/api/store-setup/design-approval`, { setup_id: setup.setup_id, franchisee_id: setup.franchisee_id, design_category: form.design_category, design_document_url: form.design_document_url || null, design_specs: { notes: form.design_notes } }, { headers: authHeaders() });
      setShowForm(false); setForm({ design_category: 'Interior', design_document_url: '', design_notes: '' }); setMessage('Design submitted for approval.'); await load();
    } catch (error) { setMessage(error.response?.data?.detail || 'Unable to submit design.'); }
  };
  const takeAction = async (approval, action) => {
    const notes = action === 'approve' ? '' : window.prompt(action === 'reject' ? 'Reason for rejection' : 'Revision notes') || '';
    if (action !== 'approve' && !notes) return;
    try { await axios.put(`${API}/api/store-setup/design-approval/${approval.approval_id}/action`, { approval_id: approval.approval_id, action, notes }, { headers: authHeaders() }); await load(); }
    catch (error) { setMessage(error.response?.data?.detail || 'Unable to update design approval.'); }
  };
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border shadow-sm p-5 flex items-center justify-between gap-3"><div><h3 className="text-lg font-semibold">Design Approvals</h3><p className="text-sm text-gray-500">Interior, signage, branding and layout approvals</p></div><button onClick={() => setShowForm(true)} className="px-4 py-2 bg-[#2b3380] text-white rounded-lg">+ Submit Design</button></div>
      {message && <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded-lg p-3 text-sm">{message}</div>}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">{loading ? <div className="p-10 text-center">Loading approvals...</div> : approvals.length === 0 ? <div className="p-10 text-center"><div className="text-4xl mb-3">🎨</div><h4 className="font-semibold">No designs submitted</h4><p className="text-sm text-gray-500">Submit the first design package for review.</p></div> : <div className="overflow-x-auto"><table className="min-w-full divide-y"><thead className="bg-gray-50"><tr><th className="p-4 text-left">Category</th><th className="p-4 text-left">Document</th><th className="p-4 text-left">Status</th><th className="p-4 text-left">Submitted</th><th className="p-4 text-right">Actions</th></tr></thead><tbody className="divide-y">{approvals.map((approval) => <tr key={approval.approval_id}><td className="p-4 font-medium">{approval.design_category}</td><td className="p-4">{approval.design_document_url ? <a className="text-[#2b3380]" href={approval.design_document_url} target="_blank" rel="noreferrer">Open document</a> : '—'}</td><td className="p-4"><span className="px-2 py-1 bg-gray-100 rounded-full text-xs">{approval.status}</span></td><td className="p-4 text-sm text-gray-500">{approval.submitted_date ? new Date(approval.submitted_date).toLocaleDateString() : '—'}</td><td className="p-4"><div className="flex justify-end gap-2">{approval.status !== 'Approved' && <><button onClick={() => takeAction(approval, 'approve')} className="text-green-700 font-medium text-sm">Approve</button><button onClick={() => takeAction(approval, 'request_revision')} className="text-amber-700 font-medium text-sm">Revise</button><button onClick={() => takeAction(approval, 'reject')} className="text-red-600 font-medium text-sm">Reject</button></>}</div></td></tr>)}</tbody></table></div>}</div>
      {showForm && <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"><form onSubmit={submit} className="bg-white rounded-xl shadow-xl w-full max-w-lg p-5 space-y-4"><div className="flex justify-between"><h3 className="text-xl font-bold">Submit Design</h3><button type="button" onClick={() => setShowForm(false)}>✕</button></div><label className="block"><span className="text-sm font-medium">Design category</span><select value={form.design_category} onChange={(e) => setForm({...form,design_category:e.target.value})} className="mt-1 w-full border rounded-lg px-3 py-2">{['Interior','Signage','Branding','Layout'].map((value) => <option key={value}>{value}</option>)}</select></label><label className="block"><span className="text-sm font-medium">Document URL</span><input type="url" value={form.design_document_url} onChange={(e) => setForm({...form,design_document_url:e.target.value})} className="mt-1 w-full border rounded-lg px-3 py-2" placeholder="https://..." /></label><label className="block"><span className="text-sm font-medium">Design notes</span><textarea value={form.design_notes} onChange={(e) => setForm({...form,design_notes:e.target.value})} rows="4" className="mt-1 w-full border rounded-lg px-3 py-2" /></label><div className="flex justify-end gap-3"><button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border rounded-lg">Cancel</button><button className="px-4 py-2 bg-[#2b3380] text-white rounded-lg">Submit</button></div></form></div>}
    </div>
  );
}

// ==================== STOCK TAB ====================
function StockTab({ setup }) {
  const [allocations, setAllocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [message, setMessage] = useState('');
  const [form, setForm] = useState({ product_category: 'Mobiles', product_name: '', allocated_qty: 1, unit_price: 0, expected_delivery_date: '' });
  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken') || localStorage.getItem('token')}` });
  const load = async () => {
    try { setLoading(true); const response = await axios.get(`${API}/api/store-setup/${setup.setup_id}/stock-allocations`, { headers: authHeaders() }); setAllocations(response.data.allocations || []); }
    catch (error) { setMessage(error.response?.data?.detail || 'Unable to load stock allocations.'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [setup.setup_id]);
  const submit = async (event) => {
    event.preventDefault();
    try {
      const product = { product_name: form.product_name, allocated_qty: Number(form.allocated_qty), unit_price: Number(form.unit_price) };
      await axios.post(`${API}/api/store-setup/stock-allocation`, { setup_id: setup.setup_id, franchisee_id: setup.franchisee_id, store_code: setup.store_code, product_category: form.product_category, products: [product], expected_delivery_date: form.expected_delivery_date }, { headers: authHeaders() });
      setShowForm(false); setForm({ product_category: 'Mobiles', product_name: '', allocated_qty: 1, unit_price: 0, expected_delivery_date: '' }); setMessage('Stock allocated successfully.'); await load();
    } catch (error) { setMessage(error.response?.data?.detail || 'Unable to allocate stock.'); }
  };
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border shadow-sm p-5 flex items-center justify-between gap-3"><div><h3 className="text-lg font-semibold">Stock Allocations</h3><p className="text-sm text-gray-500">Initial inventory planned for this store</p></div><button onClick={() => setShowForm(true)} className="px-4 py-2 bg-[#2b3380] text-white rounded-lg">+ Allocate Stock</button></div>
      {message && <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded-lg p-3 text-sm">{message}</div>}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">{loading ? <div className="p-10 text-center">Loading allocations...</div> : allocations.length === 0 ? <div className="p-10 text-center"><div className="text-4xl mb-3">📦</div><h4 className="font-semibold">No stock allocated</h4><p className="text-sm text-gray-500">Plan the first inventory allocation for launch.</p></div> : <div className="overflow-x-auto"><table className="min-w-full divide-y"><thead className="bg-gray-50"><tr><th className="p-4 text-left">Category</th><th className="p-4 text-left">Items</th><th className="p-4 text-left">Quantity</th><th className="p-4 text-left">Value</th><th className="p-4 text-left">Delivery</th><th className="p-4 text-left">Status</th></tr></thead><tbody className="divide-y">{allocations.map((item) => <tr key={item.allocation_id}><td className="p-4 font-medium">{item.product_category}</td><td className="p-4">{item.total_items}</td><td className="p-4">{item.total_quantity}</td><td className="p-4">₹{Number(item.total_value || 0).toLocaleString('en-IN')}</td><td className="p-4 text-sm">{item.expected_delivery_date ? new Date(item.expected_delivery_date).toLocaleDateString() : '—'}</td><td className="p-4"><span className="px-2 py-1 bg-gray-100 rounded-full text-xs">{item.status}</span></td></tr>)}</tbody></table></div>}</div>
      {showForm && <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"><form onSubmit={submit} className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-5 grid grid-cols-1 md:grid-cols-2 gap-4"><div className="md:col-span-2 flex justify-between"><h3 className="text-xl font-bold">Allocate Initial Stock</h3><button type="button" onClick={() => setShowForm(false)}>✕</button></div><label><span className="block text-sm font-medium">Category</span><select value={form.product_category} onChange={(e) => setForm({...form,product_category:e.target.value})} className="mt-1 w-full border rounded-lg px-3 py-2">{['Mobiles','Laptops','Tablets','Accessories'].map((value) => <option key={value}>{value}</option>)}</select></label><label><span className="block text-sm font-medium">Product name *</span><input value={form.product_name} onChange={(e) => setForm({...form,product_name:e.target.value})} required className="mt-1 w-full border rounded-lg px-3 py-2" /></label><label><span className="block text-sm font-medium">Quantity *</span><input type="number" min="1" value={form.allocated_qty} onChange={(e) => setForm({...form,allocated_qty:e.target.value})} required className="mt-1 w-full border rounded-lg px-3 py-2" /></label><label><span className="block text-sm font-medium">Unit price *</span><input type="number" min="0" step="0.01" value={form.unit_price} onChange={(e) => setForm({...form,unit_price:e.target.value})} required className="mt-1 w-full border rounded-lg px-3 py-2" /></label><label className="md:col-span-2"><span className="block text-sm font-medium">Expected delivery *</span><input type="date" value={form.expected_delivery_date} onChange={(e) => setForm({...form,expected_delivery_date:e.target.value})} required className="mt-1 w-full border rounded-lg px-3 py-2" /></label><div className="md:col-span-2 flex justify-end gap-3"><button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border rounded-lg">Cancel</button><button className="px-4 py-2 bg-[#2b3380] text-white rounded-lg">Allocate</button></div></form></div>}
    </div>
  );
}

// ==================== TRAINING TAB ====================
function TrainingTab({ setup }) {
  const [modules, setModules] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [setup]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const [modulesRes, recordsRes] = await Promise.all([
        axios.get(`${API}/api/training-modules`, { headers: { Authorization: `Bearer ${token}` }}),
        axios.get(`${API}/api/store-setup/${setup.setup_id}/training`, { headers: { Authorization: `Bearer ${token}` }})
      ]);
      
      setModules(modulesRes.data.modules || []);
      setRecords(recordsRes.data.training_records || []);
    } catch (error) {
      console.error('Error fetching training data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="text-center py-12">Loading training data...</div>;

  return (
    <div className="space-y-6">
      {/* Available Modules */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Available Training Modules</h3>
        <div className="grid grid-cols-2 gap-4">
          {modules.map(module => (
            <div key={module.module_id} className="border rounded-lg p-4 hover:border-[#2b3380] transition">
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-semibold">{module.module_name}</h4>
                <span className="text-xs bg-[#2b338030] text-[#2b3380] px-2 py-1 rounded">
                  {module.duration_hours}h
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-3">{module.description}</p>
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>{module.module_category}</span>
                <span>Pass: {module.passing_score}%</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Training Records */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Training Progress</h3>
        {records.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No training records yet. Assign modules to trainees to get started.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trainee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Module</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Score</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Certified</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {records.map(record => (
                  <tr key={record.training_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm">
                      <div className="font-medium">{record.trainee_name}</div>
                      <div className="text-gray-500">{record.trainee_role}</div>
                    </td>
                    <td className="px-6 py-4 text-sm">{record.module_name}</td>
                    <td className="px-6 py-4 text-sm">
                      <span className="px-2 py-1 text-xs rounded-full bg-[#2b338030] text-[#2b3380]">
                        {record.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-center">
                      {record.assessment_score ? `${record.assessment_score}%` : '-'}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {record.certified ? '✅' : '⏳'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== CHECKLIST TAB ====================
function ChecklistTab({ setup }) {
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchChecklist();
  }, [setup]);

  const fetchChecklist = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/store-setup/${setup.setup_id}/launch-checklist`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setChecklist(response.data);
    } catch (error) {
      console.error('Error fetching checklist:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="text-center py-12">Loading checklist...</div>;

  if (!checklist) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <p className="text-gray-600">No checklist found for this setup.</p>
      </div>
    );
  }

  const groupedItems = checklist.checklist_items.reduce((acc, item) => {
    const category = item.category || 'Other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(item);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* Progress Card */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Launch Readiness</h3>
          <div className="text-right">
            <div className="text-3xl font-bold text-[#2b3380]">{checklist.completion_percent.toFixed(0)}%</div>
            <div className="text-sm text-gray-600">
              {checklist.completed_items} of {checklist.total_items} completed
            </div>
          </div>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-4">
          <div
            className="bg-[#2b3380] h-4 rounded-full transition-all"
            style={{ width: `${checklist.completion_percent}%` }}
          ></div>
        </div>
        {checklist.launch_ready && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <span className="text-2xl mr-3">🎉</span>
              <div>
                <div className="font-semibold text-green-800">Store is Launch Ready!</div>
                <div className="text-sm text-green-600">All checklist items have been completed</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Checklist Items */}
      {Object.entries(groupedItems).map(([category, items]) => (
        <div key={category} className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">{category}</h3>
          <div className="space-y-2">
            {items.map((item, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
              >
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={item.status === 'Completed'}
                    readOnly
                    className="h-5 w-5 text-[#2b3380] rounded mr-3"
                  />
                  <span className={item.status === 'Completed' ? 'line-through text-gray-500' : ''}>
                    {item.item_name}
                  </span>
                </div>
                <span
                  className={`px-2 py-1 text-xs rounded-full ${
                    item.status === 'Completed'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}
                >
                  {item.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default StoreSetup;
